import { jwtDecode } from 'jwt-decode';
import api from './api';

interface JwtPayload {
  sub: number;
  username: string;
  permission: string;
  iat: number;
  exp: number;
}

interface RefreshTokenResponse {
  access_token: string;
}

class TokenService {
  private static readonly TOKEN_KEY = 'authToken';
  private static readonly TOKEN_BACKUP_KEY = 'authTokenBackup';
  private static readonly TOKEN_EXPIRY_BUFFER = 5 * 60; // 5 minutos em segundos
  private static refreshPromise: Promise<string> | null = null;

  // Salva o token com backup
  static setToken(token: string): void {
    try {
      sessionStorage.setItem(this.TOKEN_KEY, token);
      sessionStorage.setItem(this.TOKEN_BACKUP_KEY, token); // Backup em sessionStorage
    } catch (error) {
      console.error('Erro ao salvar token:', error);
    }
  }

  // Recupera o token com fallback para backup
  static getToken(): string | null {
    try {
      let token = sessionStorage.getItem(this.TOKEN_KEY);
      if (!token) {
        token = sessionStorage.getItem(this.TOKEN_BACKUP_KEY);
        if (token) {
          sessionStorage.setItem(this.TOKEN_KEY, token); // Restaura do backup
        }
      }
      return token;
    } catch (error) {
      console.error('Erro ao recuperar token:', error);
      return null;
    }
  }

  // Remove todos os tokens
  static clearToken(): void {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.TOKEN_BACKUP_KEY);
    } catch (error) {
      console.error('Erro ao limpar token:', error);
    }
  }

  // Verifica se o token está expirado ou próximo de expirar
  static isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp <= currentTime + this.TOKEN_EXPIRY_BUFFER;
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return true;
    }
  }

  // Atualiza o token se necessário
  static async refreshTokenIfNeeded(): Promise<string | null> {
    if (!this.isTokenExpired()) {
      return this.getToken();
    }

    // Previne múltiplas requisições de refresh simultâneas
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshToken();
      try {
        const newToken = await this.refreshPromise;
        return newToken;
      } finally {
        this.refreshPromise = null;
      }
    }

    return this.refreshPromise;
  }

  // Função para refresh do token
  private static async refreshToken(): Promise<string> {
    const oldToken = this.getToken();
    if (!oldToken) {
      throw new Error('Nenhum token disponível para refresh');
    }

    try {
      const response = await api.put<RefreshTokenResponse>('user/refresh-token', { oldToken });
      const newToken = response.data.access_token;
      this.setToken(newToken);
      return newToken;
    } catch (error) {
      console.error('Erro ao atualizar token:', error);
      this.clearToken();
      throw error;
    }
  }

  // Valida a integridade do token
  static validateToken(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      
      return Boolean(
        decoded.sub > 0 &&
        decoded.username &&
        decoded.permission &&
        decoded.exp > currentTime
      );
    } catch {
      return false;
    }
  }
}

export default TokenService; 